import { Icon } from "@iconify/react/dist/iconify.js";
import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { ProjectRole } from "$/modules/appwrite/types/collections";
import { currentUserQueryOptions } from "$/modules/auth/lib/queries";
import {
	licenseQueryOptions,
	projectRoleQueryOptions,
	projectsQueryOptions,
} from "$/modules/projects/lib/queries";
import { CreateNewProjectModal } from "$/modules/projects/ui/create-new-project-modal";
import { ProjectCard } from "$/modules/projects/ui/project-card";
import { UserDropdown } from "$/modules/projects/ui/user-dropdown";
import { Button } from "$/modules/shared/ui/button";
import { NoEntriesContainer } from "$/modules/shared/ui/no-entries";
import { PageContainer } from "$/modules/shared/ui/page-container";

export const Route = createFileRoute("/$locale/$userId/projects/")({
	component: ProjectsPage,
	loader: async ({ params, context: { queryClient } }) => {
		const [projects] = await Promise.all([
			queryClient.ensureQueryData({
				...projectsQueryOptions(params.userId),
				revalidateIfStale: true,
			}),
			queryClient.ensureQueryData({
				...licenseQueryOptions(params.userId),
				revalidateIfStale: true,
			}),
		]);
		await Promise.all(
			projects.map((project) =>
				queryClient.ensureQueryData({
					...projectRoleQueryOptions(params.userId, project),
					revalidateIfStale: true,
				}),
			),
		);
	},
});

function ProjectsPage() {
	const t = useTranslations("dashboard");
	const tRoot = useTranslations();
	const { locale, userId } = Route.useParams();
	const { data: user } = useSuspenseQuery(currentUserQueryOptions());
	const { data: projects } = useSuspenseQuery(projectsQueryOptions(userId));
	const { data: license } = useSuspenseQuery(licenseQueryOptions(userId));
	const roles = useQueries({
		queries: projects.map((project) =>
			projectRoleQueryOptions(userId, project),
		),
	});

	if (!user) {
		return (
			<div className="flex min-h-screen items-center justify-center px-4">
				<div className="flex max-w-md flex-col items-center gap-4 text-center">
					<span className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
						<Icon icon="lucide:lock" className="size-7" />
					</span>
					<h2 className="font-display text-2xl font-bold sm:text-3xl">
						{t("notLoggedIn")}
					</h2>
					<Button asChild size="lg" variant="default">
						<Link to="/$locale/signin" params={{ locale }}>
							{tRoot("signIn")}
						</Link>
					</Button>
				</div>
			</div>
		);
	}

	return (
		<PageContainer size="wide" className="max-w-[1600px]">
			<div className="mb-8 flex justify-end">
				<UserDropdown user={user} />
			</div>

			<div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<div className="min-w-0">
					<p className="mb-2 text-sm text-foreground-500">
						{t("welcome", { name: user.name || tRoot("common.userFallback") })}
					</p>
					<h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
						{t("title")}
					</h1>
					<p className="mt-2 text-foreground-500">{t("request")}</p>
					{license && (
						<p className="mt-3 text-sm font-medium text-foreground-600">
							{license.plan === "studio"
								? t("license.studio")
								: t("license.free", {
										count: license.ownedProjectCount,
										limit: license.projectLimit ?? 3,
									})}
						</p>
					)}
					{license && !license.canCreateProject && (
						<p className="mt-1 text-sm text-warning-600">
							{t("license.limitReached")}
						</p>
					)}
				</div>
				<div className="shrink-0 [&_button]:w-full sm:[&_button]:w-auto">
					<CreateNewProjectModal isLimitReached={!license?.canCreateProject} />
				</div>
			</div>

			{projects.length === 0 ? (
				<NoEntriesContainer
					icon="folder-plus"
					text={t("noProjects")}
					action={
						<CreateNewProjectModal
							isLimitReached={!license?.canCreateProject}
						/>
					}
				/>
			) : (
				<div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-4 sm:gap-6">
					{projects.map((project, index) => (
						<ProjectCard
							key={project.$id}
							project={project}
							canDeleteProject={roles[index].data === ProjectRole.Admin}
						/>
					))}
				</div>
			)}
		</PageContainer>
	);
}
